import { atom } from "jotai";
import { atomFamily } from "jotai/utils";

import { Logger } from "@moment/logging";

import { $document, docsListItemLocalEntry } from "../document-list";
import { env } from "../env";
import { fs } from "../filesystem";
import { assertSafeRelativePath } from "../paths/paths.internal";
import { getCollisionFallbackDirRelative } from "./rename-page-dir.utils";

const logger = new Logger("renamePageDir");

/**
 * Tracks the number of active page directory rename operations for a given document.
 * Used by the file watcher to suppress spurious events during rename operations.
 */
const $renamingPageCount = atomFamily((_documentId: string) => atom(0));
export const $isRenamingPage = atomFamily((documentId: string) =>
	atom((get) => get($renamingPageCount(documentId)) > 0)
);

export const $beginRenamingPage = atom(null, (_get, set, documentId: string) => {
	set($renamingPageCount(documentId), (count) => count + 1);
});

export const $endRenamingPage = atom(null, (_get, set, documentId: string) => {
	set($renamingPageCount(documentId), (count) => Math.max(0, count - 1));
});

export type RenamePageDirInput = {
	documentId: string;
	pageId: string;
	newSlug: string;
	/** Current path from page metadata (e.g., "pages/old-slug/index.md") */
	currentPath?: string;
	/** Optional explicit target directory (e.g., "pages/__tmp__page-1") */
	targetDirRelative?: string;
};

/**
 * Renames a page's directory on disk to match its slug.
 *
 * Returns the new relative path (e.g., "pages/my-cool-page/index.md") if the rename
 * was performed, or undefined if skipped.
 */
export const $renamePageDir = atom(
	null,
	async (get, set, input: RenamePageDirInput): Promise<string | undefined> => {
		const { documentId, pageId, newSlug, currentPath, targetDirRelative } = input;

		if (!env.isDesktopApp) {
			return undefined;
		}

		if (!newSlug || newSlug === "") {
			logger.debug("Empty slug, skipping rename", { documentId, pageId });
			return undefined;
		}

		const document = get($document(documentId));
		if (!document) {
			logger.debug("Document not found, skipping rename", { documentId });
			return undefined;
		}

		const local = docsListItemLocalEntry(document);
		if (!local) {
			logger.debug("Document not local, skipping rename", { documentId });
			return undefined;
		}

		const documentPath = local.path;

		// Determine the current directory name
		let currentDirRelative: string;
		if (currentPath) {
			// Strip the filename to get the directory (e.g., "pages/old-slug/index.md" -> "pages/old-slug")
			const lastSlash = currentPath.lastIndexOf("/");
			if (lastSlash < 0) {
				// Flat file at document root (e.g., "my-page.md") — no directory to rename
				logger.debug("Flat file path, skipping rename", { currentPath });
				return undefined;
			}
			currentDirRelative = currentPath.substring(0, lastSlash);
		} else {
			currentDirRelative = `pages/${pageId}`;
		}

		// Compute the new directory
		const newDirRelative = targetDirRelative ?? `pages/${newSlug}`;

		// Skip if already in the right place
		if (currentDirRelative === newDirRelative) {
			logger.debug("Directory already matches slug, skipping", {
				currentDirRelative,
				newDirRelative,
			});
			return undefined;
		}

		// Validate paths
		assertSafeRelativePath(currentDirRelative, "currentDirRelative");
		assertSafeRelativePath(newDirRelative, "newDirRelative");

		const fsApi = fs();
		const oldDirAbsolute = await get(fsApi.pathJoin([documentPath, currentDirRelative]));
		let finalDirRelative = newDirRelative;

		// Check if old directory actually exists
		const oldExists = await get(fsApi.exists({ filePath: oldDirAbsolute }));
		if (!oldExists) {
			logger.debug("Old directory doesn't exist, skipping rename", { oldDirAbsolute });
			return undefined;
		}

		// Check for collision at the target path
		let newDirAbsolute = await get(fsApi.pathJoin([documentPath, newDirRelative]));
		const targetExists = await get(fsApi.exists({ filePath: newDirAbsolute }));
		if (targetExists) {
			// Preserve explicit temp-target prefixes when adding a collision suffix.
			finalDirRelative = getCollisionFallbackDirRelative(pageId, newSlug, targetDirRelative);
			assertSafeRelativePath(finalDirRelative, "finalDirRelative");
			newDirAbsolute = await get(fsApi.pathJoin([documentPath, finalDirRelative]));

			// If even the suffixed path exists, bail
			const suffixedExists = await get(fsApi.exists({ filePath: newDirAbsolute }));
			if (suffixedExists) {
				logger.warn("Both target paths exist, skipping rename", {
					newDirRelative,
					finalDirRelative,
				});
				return undefined;
			}
		}

		// Perform the rename
		set($beginRenamingPage, documentId);
		try {
			logger.debug("Renaming page directory", {
				oldDirAbsolute,
				newDirAbsolute,
			});
			await set(fsApi.rename, {
				oldPath: oldDirAbsolute,
				newPath: newDirAbsolute,
				documentId,
			});

			// Determine the filename from current path or default to index.md
			let filename = "index.md";
			if (currentPath) {
				const lastSlash = currentPath.lastIndexOf("/");
				filename = lastSlash >= 0 ? currentPath.substring(lastSlash + 1) : currentPath;
			}

			const newPath = `${finalDirRelative}/${filename}`;
			logger.debug("Page directory renamed", { newPath });
			return newPath;
		} catch (error) {
			logger.error("Failed to rename page directory", {
				error,
				oldDirAbsolute,
				newDirAbsolute,
			});
			return undefined;
		} finally {
			set($endRenamingPage, documentId);
		}
	}
);
